import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';
import { categorySchema, merchantSchema, type Category, type Merchant } from '@shared/schemas/expenses';

export const taxonomyKeys = {
  all: ['taxonomy'] as const,
  categories: () => [...taxonomyKeys.all, 'categories'] as const,
  merchantSearch: (q: string) => [...taxonomyKeys.all, 'merchants', 'search', q] as const,
  merchantById: (id: string) => [...taxonomyKeys.all, 'merchants', id] as const,
};

// ==================== Categories ====================

/**
 * Las categorías del desplegable.
 *
 * **Sólo las aprobadas**, la misma regla que `search_merchants`,
 * `search_taxonomy` y `find_similar_*` desde `20260830160000`: una sola
 * respuesta a «¿qué taxonomía existe?» en lugar de cuatro parecidas.
 *
 * Antes aceptaba también las `pending`, «para que una categoría recién creada
 * pueda usarse antes de moderarse». Medido el 2026-08-30 antes de cambiarlo:
 * **cero categorías pendientes en producción**, así que ese caso no existía y
 * el filtro no tapa nada — sólo cierra la puerta.
 *
 * **Esto es una consulta a la tabla, no una RPC**, y por eso el filtro de lo que
 * crean las cuentas de pruebas tiene que estar **aquí** y no en el servidor:
 * `20260815121023` hace que esas filas nazcan `pending`, y `is_test` es la
 * puerta de verdad porque una fila puede nacer aprobada por DEFAULT sin que
 * nadie la mire. Ver #33 y ADR-0060.
 */
export async function fetchAllCategories(): Promise<Category[]> {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('moderation_status', 'approved')
    .eq('is_test', false)
    .order('is_predefined', { ascending: false })
    .order('name', { ascending: true });

  if (error) {
    logger.error('fetchAllCategories failed', { code: error.code });
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => categorySchema.parse(row));
}

// ==================== Merchants ====================

/**
 * Búsqueda fuzzy de comercios usando pg_trgm.
 * El operador % de similarity con índice GIN devuelve matches "cercanos".
 * Se ordenan por similarity desc y frecuencia (simplificada por created_at por ahora).
 */
export async function searchMerchants(query: string, limit = 10): Promise<Merchant[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  // Fuzzy matching real vía RPC search_merchants (pg_trgm similarity + prefix match).
  // Tolera typos como "mergadona" → "mercadona".
  const { data, error } = await supabase.rpc('search_merchants', {
    p_query: q,
    p_limit: limit,
  });

  if (error) {
    logger.error('searchMerchants failed', { code: error.code });
    throw new Error(error.message);
  }
  // La RPC devuelve una columna extra `score` — la ignoramos con parse
  return ((data ?? []) as Merchant[]).map((row) => merchantSchema.parse(row));
}
